import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { 
    Container, Box, Typography, TextField, Button, Alert, Paper, 
    Link, InputAdornment
} from '@mui/material';
import { Email, LockReset } from '@mui/icons-material';
import api from '../../api/axios';

const ForgotPassword = () => {
    const [email, setEmail] = useState('');
    const [submitted, setSubmitted] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        try {
            await api.post('/auth/forgot-password', { email });
            setSubmitted(true);
        } catch (err) {
            // We usually don't want to reveal if email exists, but for now generic error
            setError('An error occurred. Please try again.');
        }
        setLoading(false);
    };

    if (submitted) {
        return (
            <Box sx={{ 
                minHeight: '100vh', 
                display: 'flex', 
                alignItems: 'center', 
                bgcolor: '#F8FAFC',
                backgroundImage: 'radial-gradient(#E2E8F0 1px, transparent 1px)',
                backgroundSize: '20px 20px'
            }}>
                <Container component="main" maxWidth="xs">
                    <Paper elevation={0} sx={{ p: 4, borderRadius: 3, border: '1px solid #E2E8F0', textAlign: 'center' }}>
                        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center' }}>
                            <Box sx={{ p: 2, bgcolor: 'success.light', borderRadius: '50%', color: 'success.dark' }}>
                                <Email fontSize="large" />
                            </Box>
                        </Box>
                        <Typography variant="h5" fontWeight={700} gutterBottom>Check your email</Typography>
                        <Typography color="text.secondary" paragraph>
                            If an account exists for <strong>{email}</strong>, we have sent a password reset link.
                        </Typography>
                        <Button component={RouterLink} to="/login" variant="outlined" fullWidth sx={{ mt: 2 }}>
                            Back to Login
                        </Button>
                    </Paper>
                </Container>
            </Box>
        );
    }

    return (
        <Box sx={{ 
            minHeight: '100vh', 
            display: 'flex', 
            alignItems: 'center', 
            bgcolor: '#F8FAFC',
            backgroundImage: 'radial-gradient(#E2E8F0 1px, transparent 1px)',
            backgroundSize: '20px 20px'
        }}>
            <Container component="main" maxWidth="xs">
                <Paper 
                    elevation={0} 
                    sx={{ 
                        p: 4, 
                        display: 'flex', 
                        flexDirection: 'column', 
                        alignItems: 'center',
                        border: '1px solid #E2E8F0',
                        borderRadius: 3,
                        bgcolor: 'white',
                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                    }}
                >
                    <Box sx={{ mb: 3, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <Box sx={{ 
                            width: 56, height: 56, bgcolor: 'primary.light', borderRadius: '16px',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2,
                            color: 'primary.main'
                        }}>
                            <LockReset fontSize="large" />
                        </Box>
                        <Typography component="h1" variant="h5" fontWeight={800} color="text.primary">
                            Forgot Password
                        </Typography>
                        <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mt: 1 }}>
                            Enter your email to receive a reset link
                        </Typography>
                    </Box>

                    {error && <Alert severity="error" sx={{ width: '100%', mb: 3 }}>{error}</Alert>}

                    <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            id="email"
                            label="Email Address"
                            name="email"
                            autoComplete="email"
                            autoFocus
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            disabled={loading}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Email color="action" fontSize="small" />
                                    </InputAdornment>
                                ),
                                sx: { borderRadius: 2 }
                            }}
                        />
                        <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            size="large"
                            sx={{ mt: 3, mb: 3, py: 1.5, fontWeight: 700, borderRadius: 2 }}
                            disabled={loading}
                        >
                            {loading ? 'Sending...' : 'Send Reset Link'}
                        </Button>
                        <Box sx={{ textAlign: 'center' }}>
                            <Link component={RouterLink} to="/login" variant="body2" underline="hover" fontWeight={600}>
                                Back to Login
                            </Link>
                        </Box>
                    </Box>
                </Paper>
            </Container>
        </Box>
    );
};

export default ForgotPassword;
